import bs58 from 'bs58';

import { useAccount } from 'shared/hooks/use-web3';
import { useGlobalStore } from 'shared/store/globalStore';

export const useHash = () => {
  const { address } = useAccount();
  const { jwt, jwtValidUntil } = useGlobalStore();
  const handleSetHash = async ({ hashKey, hashValue }: { hashKey: string; hashValue: string }) => {
    try {
      let date = jwtValidUntil[address!];

      if (!date) {
        return { ok: false };
      }
      let message = `address: ${address} \nvalid until: ${new Date(date).toISOString()}`;

      const messageBytes = new TextEncoder().encode(message);
      const base58Message = bs58.encode(messageBytes);
      const response = await fetch('/api/hash', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          hashKey,
          hashValue,
          address,
          signature: jwt[address!],
          message: base58Message,
        }),
      });

      return response;
    } catch (error) {
      console.error('Error:', error);
      return { ok: false };
    }
  };

  const handleGetHash = async ({ hashKey }: { hashKey: string }) => {
    const maxRetries = 3;
    const baseDelay = 1000;
    let attempt = 0;
    let date = jwtValidUntil[address!];

    if (!date) {
      return;
    }

    let message = `address: ${address} \nvalid until: ${new Date(date).toISOString()}`;

    // Using Base58 encoding instead of URL encoding
    const messageBytes = new TextEncoder().encode(message);
    const base58Message = bs58.encode(messageBytes);

    while (attempt <= maxRetries) {
      try {
        const params = new URLSearchParams({
          hashKey,
          message: base58Message,
          signature: jwt[address!],
        });

        const response = await fetch(`/api/hash?${params.toString()}`, {
          method: 'GET',
        });

        if (response?.ok) {
          const data = await response.json();
          if (data.value) {
            return data.value;
          } else {
            return null;
          }
        } else if (response.status >= 500) {
          console.error(`Attempt ${attempt + 1}: Server returned status ${response.status}`);
        } else {
          console.error(
            `Attempt ${attempt + 1}: Server returned status ${
              response.status
            }. No retry will be attempted.`,
          );
          break;
        }
      } catch (error) {
        console.error(`Attempt ${attempt + 1}: Error fetching hash -`, error);
      }

      attempt++;
      if (attempt < maxRetries) {
        const delay = baseDelay * Math.pow(2, attempt - 1);
        console.log(`Retrying in ${delay} ms...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
      } else {
        console.error('Max retries reached. Unable to retrieve hash.');
      }
    }
    return null;
  };

  return {
    handleSetHash,
    handleGetHash,
  };
};
